'use client';

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { MicrophoneIcon, StopIcon } from '@heroicons/react/24/outline';

export type SpeechRecognitionResultEvent = {
  resultIndex: number;
  results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
};
export type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionResultEvent) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
};
type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

export type AssistantDictationHandle = { stop(): void; cancel(): void };
type AssistantDictationProps = { getDraft(): string; setDraft(value: string): void };

export const AssistantDictation = forwardRef<AssistantDictationHandle, AssistantDictationProps>(function AssistantDictation({ getDraft, setDraft }, ref) {
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const [supported, setSupported] = useState<boolean | null>(null);
  const [recording, setRecording] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [message, setMessage] = useState('');
  useEffect(() => {
    const speechWindow = window as unknown as { SpeechRecognition?: SpeechRecognitionConstructor; webkitSpeechRecognition?: SpeechRecognitionConstructor };
    setSupported(Boolean(speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition));
    return () => {
      const recognition = recognitionRef.current;
      if (!recognition) return;
      recognition.onend = null;
      recognition.onerror = null;
      recognition.onresult = null;
      recognition.abort();
      recognitionRef.current = null;
    };
  }, []);
  const stop = () => {
    const recognition = recognitionRef.current;
    if (!recognition) return;
    setStopping(true);
    recognition.stop();
    setRecording(false);
    setMessage('Stopping dictation…');
  };
  const cancel = () => {
    const recognition = recognitionRef.current;
    if (!recognition) return;
    recognitionRef.current = null;
    recognition.onend = null;
    recognition.onerror = null;
    recognition.onresult = null;
    recognition.abort();
    setRecording(false);
    setStopping(false);
  };
  useImperativeHandle(ref, () => ({ stop, cancel }), []);
  const start = () => {
    if (recognitionRef.current) return;
    const speechWindow = window as unknown as { SpeechRecognition?: SpeechRecognitionConstructor; webkitSpeechRecognition?: SpeechRecognitionConstructor };
    const Recognition = speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition;
    if (!Recognition) { setSupported(false); return; }
    const recognition = new Recognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = navigator.language || 'en-US';
    recognition.onresult = (event) => {
      const chunks: string[] = [];
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const item = event.results[index];
        if (item?.isFinal && item[0]?.transcript) chunks.push(item[0].transcript.trim());
      }
      if (!chunks.length) return;
      const current = getDraft();
      const addition = chunks.join(' ');
      setDraft(current.trim() ? `${current.trimEnd()} ${addition}` : addition);
      setMessage('Dictation added to your draft. You can edit it before sending.');
    };
    recognition.onerror = (event) => {
      recognitionRef.current = null;
      recognition.onresult = null;
      recognition.onend = null;
      recognition.onerror = null;
      recognition.abort();
      setRecording(false);
      setStopping(false);
      setMessage(event.error === 'not-allowed' || event.error === 'service-not-allowed'
        ? 'Microphone access was blocked. Check your browser permission.'
        : 'Dictation stopped. You can keep editing your draft.');
    };
    recognition.onend = () => {
      if (recognitionRef.current === recognition) {
        recognitionRef.current = null;
        setMessage('Dictation stopped. Edit your draft before sending.');
      }
      setRecording(false);
      setStopping(false);
    };
    recognitionRef.current = recognition;
    setMessage('Listening. Select Stop dictation when you are done.');
    setRecording(true);
    try { recognition.start(); } catch {
      recognitionRef.current = null;
      setRecording(false);
      setMessage('Dictation could not start. Check your microphone permission.');
    }
  };
  if (supported === false) return <span className="grid size-12 shrink-0 place-items-center text-[var(--ss-text-muted)] opacity-50" role="img" aria-label="Dictation is unavailable in this browser." title="Dictation is unavailable in this browser."><MicrophoneIcon className="size-5" aria-hidden="true" /></span>;
  return <>
    <button type="button" disabled={supported === null || stopping} onClick={recording ? stop : start} aria-label={recording ? 'Stop dictation' : stopping ? 'Stopping dictation' : 'Start dictation'} title={recording ? 'Stop dictation' : stopping ? 'Stopping dictation' : 'Dictate a message'} className={`grid size-12 shrink-0 place-items-center rounded-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] disabled:opacity-50 ${recording ? 'text-[var(--ss-critical)]' : 'text-[var(--ss-text-muted)] hover:bg-[var(--ss-surface-subtle)]'}`}>
      {recording ? <StopIcon className="size-5" aria-hidden="true" /> : <MicrophoneIcon className="size-5" aria-hidden="true" />}
    </button>
    {message ? <span className="basis-full px-2 text-xs text-[var(--ss-text-muted)]" role="status" aria-live="polite">{message}</span> : null}
  </>;
});
